import { DateTimeUtils } from '../media-player/date-time-utils';
import { EventEmitterBase } from './event-emitter-base';
import { EventEnvelop, EventEnvelopData, EventEnvelopMetadata, EventKey } from './event-sourcing';
import { MessageBus } from './message-bus';
import { IError, ISpanSnapshot } from './models';
import { deep_merge } from './object-manipulation';
import { Queue } from './queue';

const getTime: () => number = performance?.now ?? Date.now;

let SequenceId: number = 0;

export class EventStream extends EventEmitterBase {

    id: string;
    name: string;
    spanId: string;
    spanType: string;
    version: number = 0;
    messageBus: MessageBus;

    //TODO: Use milliseconds from epoch?
    lastUpdate: number;
    startDate: number;

    ended: boolean;

    protected logs: { [key: string]: any } = {};
    protected tags: { [key: string]: string } = {};
    protected errors: Queue<IError> = new Queue<IError>();
    protected marks: { [key: string]: number } = {};

    protected depth: number = 0;
    protected parent: EventStream = null;
    protected children: EventStream[] = [];
    protected events: Queue<EventEnvelop> = new Queue<EventEnvelop>();
    protected context: { [id: string]: any };

    private _started: number;
    private _ended: number;
    private duration: number = 0;

    get size(): number {
        return this.events.size;
    }

    constructor(spanType: string, spanId: string, context: { [id: string]: any } = {}, parent: EventStream = null) {
        super();
        this.version = 0;
        this.spanType = spanType;
        this.spanId = spanId;
        this.id = [this.spanType, this.spanId].join(':');

        this.context = context;

        if (parent !== null) {
            this.depth = parent.depth + 1;
            this.parent = parent;
        } else {
            this.depth = 0;
        }

        this.messageBus = MessageBus.GetInstance();
        EventStream.add(this);

        this.on('stream.change', () => this.update());

        this.on('start', () => {
            const now: number = Date.now();

            this._started = now;
            this.startDate = now;
            this.lastUpdate = now;
            this.ended = false;
            this.duration = 0;
            EventStream.root.flush();
        });

        this.on('end', async () => {
            this._ended = Date.now();
            // this.lastUpdate = DateTimeUtils.getISODateTime();
            this.lastUpdate = this._ended;
            this.duration = this._ended - this._started;
            this.ended = true;
            EventStream.root.flush();
        });
    }

    * getAncestors(): Iterator<EventStream, void> {
        let node: EventStream = this.parent;
        while (node) {
            yield node;
            node = node.parent;
        }
    }

    * getEvents(): Iterator<{ stream: EventStream, event: EventEnvelop }, void> {
        const stack: EventStream[] = [this];
        while (stack.length > 0) {
            const stream: EventStream = stack.pop();
            for (let event of stream.events.getItems()) {
                yield {stream, event};
            }
            for (let child of stream.children) {
                stack.push(child);
            }
        }
    }

    update(): void {
        const now: number = Date.now();
        this.lastUpdate = now;
        this.duration = now - this._started;
        if (this.parent !== null) {
            this.parent.update();
        }
    }

    inject(context: { [key: string]: any }): void {
        this.context = deep_merge(this.context, context);
    }

    append(eventType: EventKey, eventData: EventEnvelopData = {}, metadata: EventEnvelopMetadata = {}): void {
        const event: EventEnvelop = new EventEnvelop(eventType, eventData);
        this.version++;
        this.lastUpdate = DateTimeUtils.UTCNow;
        // this.lastUpdate = DateTimeUtils.getISODateTime();
        event.sequenceId = (++SequenceId);

        metadata = metadata || {};
        metadata = {...this.context, ...metadata};

        event.context = {
            ...event.context,
            ...metadata,
        };

        this.events.enqueue(event);
        this.emit('stream.change', {
            stream: this,
            event,
        });
    }

    /**
     *
     * @param type EventKey
     * @param data EventEnvelopData
     * @param metadata
     */
    appendAndFlush(type: EventKey, data: EventEnvelopData = {}, metadata: EventEnvelopMetadata = {}): void {
        this.append(type, data, metadata);
        this.flush();
    }

    flush(force: boolean = false): void {
        EventStream.root.emit('flush', {
            root: EventStream.root,
            force: force,
        });
    }

    clear(): void {
        this.events.clear();
        this.children.forEach(child => {
            if (child.ended) {
                this.removeChild(child);
            } else {
                child.clear();
            }
        });
    }

    createStream(name: string, id: string, context: { [id: string]: any } = {}): EventStream {
        const stream: EventStream = new EventStream(name, id, context, this);
        stream.inject({...context});
        this.children.push(stream);
        return stream;
    }

    start(resume: boolean = false): EventStream {
        this.emit('start');
        if (resume) {
            this.append(EventKey.SpanResumed, this.getSpanMetadata());
        } else {
            this.append(EventKey.SpanStarted, this.getSpanMetadata());
        }
        return this;
    }

    end(): void {
        if (this.ended) {
            return;
        }
        this.ended = true;
        this._ended = Date.now();
        this.children.forEach(c => c.end());
        this.append(EventKey.SpanEnded, this.getSpanMetadata());
        this.emit('end', this);
    }

    log(key: string, object): void {
        this.logs[key] = object;
    }

    tag(key: string, value): void {
        this.tags[key] = value;
    }

    mark(key: string): void {
        this.marks[key] = getTime();
    }

    error(error: Error): void {
        this.errors.enqueue({
            message: error.message,
            stack: error.stack,
            name: error.name,
        });
    }

    getSnapshot(): ISpanSnapshot {
        const context: { [id: string]: any } = {...this.context};

        const events: EventEnvelop[] = this.events.getItems().map((e: EventEnvelop) => {
            const event = {...e};
            delete event['context'];
            return event;
        });

        const duration: number = this.ended
            ? this.duration
            : Date.now() - this._started;

        const snapshot: ISpanSnapshot = {
            id: this.id,
            type: this.spanType,
            name: this.name,
            events: [...events],
            context: {...context},
            children: this.children.map(child => child.getSnapshot()),
            tags: {...this.tags},
            marks: {...this.marks},
            errors: [...this.errors.getItems()],
            ended: this.ended === true,
            //TODO: Use milliseconds from epoch?
            startDate: this.startDate,
            lastUpdate: this.lastUpdate,
            duration: duration,
            version: this.version || 0,
        };

        for (let key of Object.keys(snapshot)) {
            if (typeof snapshot[key] === 'object' && !(snapshot[key] instanceof Array)) {
                if (snapshot[key] === null || (snapshot[key] !== null && Object.keys(snapshot[key]).length === 0)) {
                    delete snapshot[key];
                }
            }

            if (snapshot[key] instanceof Array && snapshot[key].length === 0) {
                delete snapshot[key];
            }
        }

        return snapshot;
    }

    removeChild(child: EventStream): void {
        const index: number = this.children.indexOf(child);
        child.emit('beforeRemove', child);
        EventStream.remove(child);
        if (index > -1) {
            this.children.splice(index, 1);
        } else {
            console.warn(' - not found');
        }
    }

    getChild(id: string): EventStream {
        return this.children.find(c => c.id === id);
    }

    static getEventCount = () => {
        const streams: EventStream[] = Object.values(EventStream.tree);
        let sum: number = 0;
        for (let s of streams) {
            sum += s.size;
        }
        return sum;
    };

    static root: EventStream = null;

    static tree: { [key: string]: EventStream } = {};

    static getStream(type: string, id: string): EventStream {
        let stream_id: string = `${type}:${id}`;
        if (!id) {
            stream_id = type;
        }
        const stream: EventStream = EventStream.tree[stream_id];
        if (stream) {
            return stream;
        }
        return null;
    }

    static add(stream: EventStream): EventStream {
        EventStream.tree[stream.id] = stream;
        if (EventStream.root === null && stream.parent === null) {
            EventStream.root = stream;
        }
        return stream;
    }

    static remove(stream: EventStream): void {
        if (EventStream.tree[stream.id]) {
            delete EventStream[stream.id];
        }
    }

    private getSpanMetadata(): EventEnvelopData {
        return {
            spanId: this.spanId,
            type: this.spanType,
            ended: this.ended,
            startDate: this.startDate,
            lastUpdate: this.lastUpdate,
            duration: this.duration,
        };
    }
}
